/**
 * forest_client.ts — main-thread wrapper that posts fit jobs to the worker
 * and swaps the result into the live forest when it returns.
 *
 * The bandit's `fitFn` hook is wired here (instead of the default sync
 * fitter) so the UI's tick loop never blocks on a fit. The bandit keeps
 * predicting with the stale forest until the fresh one arrives, then the
 * next select() call sees the updated trees.
 *
 * The harness (which runs entirely inside a worker already) uses the bandit's
 * default sync fitFn — no point posting worker-to-worker.
 */

import type { KitchenBandit, BanditHyperparams } from './bandit';
import type { TreeBuffers } from './tree';
import { N_FEATURES } from './constants';
import type { FitRequest } from './worker';

export class ForestClient {
  private worker: Worker | null = null;
  private pendingId = 0;
  private nextId = 1;
  /** Diagnostic counters surfaced to the HUD. */
  fitsRequested = 0;
  fitsCompleted = 0;
  lastFitMs = 0;

  constructor(private bandit: KitchenBandit) {
    // Lazily create the worker on first fit request so the page boots
    // without spinning up the worker thread until it's needed.
  }

  private ensureWorker(): Worker {
    if (this.worker) return this.worker;
    // Vite's worker import — `new Worker(new URL('./worker.ts', import.meta.url), { type: 'module' })`
    // produces a bundled worker in dev and a separate chunk in prod.
    this.worker = new Worker(new URL('./worker.ts', import.meta.url), { type: 'module' });
    this.worker.onmessage = (e: MessageEvent) => {
      const msg = e.data;
      if (msg.kind !== 'fitted') return;
      // Ignore stale results — only the latest fit's trees are loaded.
      if (msg.id !== this.pendingId) return;
      this.bandit.forest.loadSerialized(msg.trees as TreeBuffers[]);
      this.fitsCompleted++;
    };
    return this.worker;
  }

  /**
   * Install this client as the bandit's fitFn. After this call, every
   * observe() that crosses the refit cadence posts a job to the worker
   * instead of fitting synchronously on the main thread.
   */
  install(): void {
    this.bandit.fitFn = (X: Float64Array, y: Float64Array, hp: BanditHyperparams) => {
      const worker = this.ensureWorker();
      const id = this.nextId++;
      this.pendingId = id;
      this.fitsRequested++;
      const t0 = performance.now();
      const req: FitRequest = {
        kind: 'fit',
        id,
        X,
        y,
        hp,
        nFeatures: N_FEATURES,
      };
      // Track the round-trip time (the worker posts back the transferred X/y;
      // we measure from post to onmessage).
      const prevHandler = worker.onmessage;
      worker.onmessage = (e: MessageEvent) => {
        const msg = e.data;
        if (msg.kind === 'fitted' && msg.id === id) {
          this.lastFitMs = performance.now() - t0;
        }
        if (prevHandler) prevHandler.call(worker, e);
      };
      // Transfer X and y (zero-copy). The worker transfers them back.
      worker.postMessage(req, [X.buffer, y.buffer] as Transferable[]);
    };
  }

  /** Tear down the worker (on reset / between runs). */
  dispose(): void {
    if (this.worker) {
      this.worker.terminate();
      this.worker = null;
    }
    this.pendingId = 0;
  }
}
